// ============================================================================
// LE MENU « NOUVEL ARTICLE » — les trois portes de création (relevé maquette l.843-880).
//
// ⚠️ LES TROIS SONT MONTRÉES, MÊME CELLES QUI N'EXISTENT PAS ENCORE. J'avais d'abord câblé le bouton
// directement sur la seule porte ouverte, au motif qu'offrir une option non implémentée est un défaut
// (§23.0). C'était un raisonnement juste appliqué au mauvais endroit : masquer deux portes ne dit pas
// « pas encore », ça dit « ça n'existe pas » — et l'utilisateur cherche alors une fonction qu'il sait
// prévue. La politique du dépôt tranche ce cas : « zéro bouton mort », donc un STUB À TOAST EXPLICITE.
// La porte est visible, son état est LISIBLE avant le clic, et le clic dit ce qui manque.
//
// ⚠️ IL S'ADOSSE À `Popover` et ne réécrit pas un panneau ancré : Échap, clic extérieur, focus rendu
// au déclencheur et bornage à la fenêtre y sont déjà. Un menu réécrit à la main, c'est réécrire ces
// oublis-là.
// ============================================================================
import { Popover, showToast, rovingMenuEntry, rovingMenuItem } from '@orgazone/ui-web';
import { key, t, type Translatable } from '../i18n';

export type PorteCreation = 'simple' | 'gamme' | 'import';

/** Une porte : sa pastille, son nom, sa promesse — et si elle est ouverte. */
const PORTES: {
  key: PorteCreation;
  heading: Translatable;
  sous: Translatable;
  fond: string;
  encre: string;
  tracé: React.ReactNode;
  ouverte: boolean;
}[] = [
  {
    key: 'simple',
    heading: key('Article simple'),
    sous: key('Une déclinaison. Quatre champs et c’est vendable.'),
    fond: 'var(--teal-100)', encre: 'var(--teal-700)', ouverte: true,
    tracé: <><path d="M3 12V4h8l10 10-8 8L3 12z" /><circle cx="8" cy="8" r="1.5" /></>,
  },
  {
    key: 'gamme',
    heading: key('Gamme complète'),
    sous: key('Toutes les tailles et couleurs, codes-barres et quantités.'),
    fond: 'var(--violet-bg)', encre: 'var(--violet-fg)', ouverte: true,
    tracé: <>
      <rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" />
    </>,
  },
  {
    key: 'import',
    heading: key('Importer un fichier'),
    sous: key('Excel ou CSV, avec un masque réutilisable par fournisseur.'),
    fond: 'var(--blue-bg)', encre: 'var(--blue-fg)', ouverte: true,
    tracé: <path d="M12 20V8m0 0l-4 4m4-4l4 4M4 4h16" />,
  },
];

export function MenuNouvelArticle({ open, onOpenChange, onChoisir }: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onChoisir: (porte: PorteCreation) => void;
}) {
  return (
    <Popover
      open={open}
      onOpenChange={onOpenChange}
      id="bo-new-article"
      label={t('Créer un article')}
      role="menu"
      // ⚠️ LE PANNEAU A LE FOCUS À L'OUVERTURE : ↓ y entre sur la première porte, ↑ sur la dernière.
      onPanelKeyDown={(e) => rovingMenuEntry(e)}
      geom={{ width: 326, align: 'end' }}
      trigger={(api) => (
        <button
          {...api}
          type="button"
          style={{
            height: 40, padding: '0 16px', border: 'none', borderRadius: 'var(--r-sm)',
            background: 'var(--teal-500)', color: '#fff', fontSize: '13.5px', fontWeight: 700,
            display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap', cursor: 'pointer',
            // Relevé l.848 — l'ombre teintée, qui détache le bouton d'appel du reste de l'en-tête.
            boxShadow: '0 6px 18px -8px var(--teal-500)',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M12 5v14M5 12h14" />
          </svg>
          
          {t('Nouvel article')}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.8 }} aria-hidden>
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      )}
    >
      {({ close }) => (
        <div role="none">
          {PORTES.map((p, i) => (
            <button
              key={p.key}
              type="button"
              role="menuitem"
              {...rovingMenuItem({ index: i, count: PORTES.length })}
              // ⚠️ `aria-disabled` ET NON `disabled` : un élément désactivé sort de la tabulation, donc
              // au clavier la porte à venir devient INVISIBLE — c'est justement ce qu'on voulait éviter.
              aria-disabled={!p.ouverte || undefined}
              onClick={() => {
                close();
                if (p.ouverte) { onChoisir(p.key); return; }
                // Stub À TOAST EXPLICITE : le clic ne fait pas semblant, il DIT ce qui manque.
                showToast(t('« {heading} » n’est pas encore disponible — la porte est prévue, elle n’est pas construite.', { heading: t(p.heading) }), 'warn');
              }}
              className="oz-new-art-item"
              style={{
                display: 'flex', gap: 13, width: '100%', padding: '14px 16px', border: 'none',
                borderBottom: i < PORTES.length - 1 ? '1px solid var(--ink-100)' : 'none',
                background: 'transparent', textAlign: 'left', cursor: 'pointer',
                opacity: p.ouverte ? 1 : 0.62,
              }}
            >
              <span style={{
                width: 34, height: 34, borderRadius: 'var(--r-ctrl)', flexShrink: 0,
                background: p.fond, color: p.encre,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  {p.tracé}
                </svg>
              </span>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '13.5px', fontWeight: 600, color: 'var(--ink-900)' }}>
                  {t(p.heading)}
                  {/* ⚠️ L'ÉTAT SE LIT AVANT LE CLIC. Un toast qui apprend après coup qu'une porte est
                      fermée fait perdre le geste ; la mention, elle, évite de le faire. */}
                  {!p.ouverte && (
                    <span style={{
                      height: 18, padding: '0 7px', borderRadius: 'var(--r-pill)',
                      background: 'var(--ink-100)', color: 'var(--ink-500)',
                      fontSize: 'var(--t-micro)', fontWeight: 700, display: 'inline-flex', alignItems: 'center',
                    }}>{t('bientôt')}</span>
                  )}
                </span>
                <span style={{ display: 'block', fontSize: '12px', color: 'var(--ink-500)', marginTop: 2, lineHeight: 1.45 }}>
                  {t(p.sous)}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </Popover>
  );
}
